import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useRoute } from "wouter";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { StatusBadge } from "@/components/shared/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, RefreshCw, Send, FileSpreadsheet } from "lucide-react";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/format";

interface InvoiceLine {
  id: string;
  serviceTypeLabel?: string | null;
  serviceTypeKey?: string | null;
  revenueCategory: string;
  quantity: string;
  unitAmount: string;
  lineAmount: string;
  costAmount: string;
  xeroRevenueAccountCode?: string | null;
}

interface XeroSyncEvent {
  id: string;
  status: string;
  errorMessage?: string | null;
  createdAt: string;
}

interface InvoiceDetail {
  id: string;
  clientName: string;
  revenueOwnerName: string;
  revenueMonth: string;
  status: string;
  xeroSyncStatus: string;
  currency: string;
  subtotalAmount: string;
  taxAmount: string;
  totalAmount: string;
  issuedAt: string;
  revenuePeriodStart: string;
  revenuePeriodEnd: string;
  xeroLastError?: string | null;
  lines: InvoiceLine[];
  syncEvents: XeroSyncEvent[];
}

export default function InvoiceDetailPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [, params] = useRoute("/invoices/:id");
  const invoiceId = params?.id;

  const { data: invoice, isLoading } = useQuery<InvoiceDetail>({
    queryKey: ["/api/invoices", invoiceId],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!invoiceId,
  });

  const pushMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/invoices/${invoiceId}/push-to-xero`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (q) => (q.queryKey[0] as string)?.startsWith("/api/invoices") });
      toast({ title: "Invoice pushed to Xero" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to push invoice",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const lineColumns: ColumnDef<InvoiceLine>[] = [
    {
      key: "serviceType",
      header: "Service Type",
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {row.serviceTypeLabel || row.serviceTypeKey || "Unlabelled"}
          </span>
          <span className="text-xs text-muted-foreground capitalize">
            {row.revenueCategory.replace(/_/g, " ")}
          </span>
        </div>
      ),
    },
    {
      key: "quantity",
      header: "Quantity",
      cell: (row) => row.quantity,
    },
    {
      key: "unitAmount",
      header: "Unit Amount",
      cell: (row) => formatCurrency(row.unitAmount),
    },
    {
      key: "lineAmount",
      header: "Revenue",
      cell: (row) => formatCurrency(row.lineAmount),
    },
    {
      key: "costAmount",
      header: "Cost",
      cell: (row) => formatCurrency(row.costAmount),
    },
    {
      key: "xeroAccount",
      header: "Xero Account",
      cell: (row) => row.xeroRevenueAccountCode || "—",
    },
  ];

  const syncColumns: ColumnDef<XeroSyncEvent>[] = [
    {
      key: "status",
      header: "Status",
      cell: (row) => <StatusBadge type="xero" status={row.status} />,
    },
    {
      key: "createdAt",
      header: "Timestamp",
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      key: "error",
      header: "Message",
      cell: (row) => row.errorMessage || "—",
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-[200px] rounded-lg" />
        <Skeleton className="h-[240px] rounded-lg" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate("/invoices")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Invoices
        </Button>
        <div className="py-12 text-center">
          <h2 className="text-xl font-semibold">Invoice not found</h2>
          <p className="mt-1 text-muted-foreground">
            The requested invoice could not be loaded.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <Button variant="ghost" className="px-0" onClick={() => navigate("/invoices")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Invoices
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-heading">{invoice.clientName}</h1>
            <p className="text-sm text-muted-foreground">
              Revenue month {invoice.revenueMonth} · Owner {invoice.revenueOwnerName}
            </p>
          </div>
        </div>

        <Button onClick={() => pushMutation.mutate()} disabled={pushMutation.isPending}>
          {pushMutation.isPending ? (
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Send className="mr-2 h-4 w-4" />
          )}
          Push to Xero
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Invoice Status</CardTitle>
          </CardHeader>
          <CardContent>
            <StatusBadge type="invoice" status={invoice.status} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Xero Sync</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <StatusBadge type="xero" status={invoice.xeroSyncStatus} />
            {invoice.xeroLastError && (
              <p className="text-xs text-red-600">{invoice.xeroLastError}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Revenue Period</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              {formatDate(invoice.revenuePeriodStart)} - {formatDate(invoice.revenuePeriodEnd)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-heading">
              {formatCurrency(invoice.totalAmount, invoice.currency)}
            </p>
            <p className="text-xs text-muted-foreground">
              Issued {formatDate(invoice.issuedAt)}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Invoice Lines</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={lineColumns}
            data={invoice.lines}
            rowKey={(row) => row.id}
            emptyIcon={FileSpreadsheet}
            emptyTitle="No invoice lines"
            emptyDescription="This invoice does not contain any lines."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Sync History</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={syncColumns}
            data={invoice.syncEvents}
            rowKey={(row) => row.id}
            emptyTitle="No sync attempts"
            emptyDescription="This invoice has not been pushed to Xero yet."
          />
        </CardContent>
      </Card>
    </div>
  );
}
